import React, { useState } from 'react';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  Bars3Icon,
  PlayIcon,
  ClockIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { ActionDropdown } from './ActionDropdown';
import { OverflowMenu } from './OverflowMenu';
import type { CaseHeaderProps, ActionType, OverflowActionType } from './CaseHeader.types';
import './CaseHeader.styles.css';

/**
 * CaseHeader Component
 *
 * Sticky header with 3-zone layout:
 * - Left zone: Back button (own column) + Identity (title + meta)
 * - Middle zone: Spacer
 * - Right zone: Assignee + Actions
 *
 * Rail alignment:
 * - Title text aligns with search input (Rail A)
 * - Actions align with sidebar right edge (Rail D)
 */
export const CaseHeader: React.FC<CaseHeaderProps> = ({
  affaireId,
  status: _status,
  missionType: _missionType,
  address: _address,
  amount: _amount,
  lastActivityDate,
  assignee,
  isFrozen,
  freezeInfo,
  onBack,
  onAction,
  onOverflowAction,
  onChangeAssignee,
  onReactivate,
  onToggleSidebar,
  sidebarOpen: _sidebarOpen,
  activeTab = 'activity',
  onTabChange,
  isSubscribed,
}) => {
  // Dropdown states
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [_assigneeDropdownOpen, _setAssigneeDropdownOpen] = useState(false);

  // Handle action selection
  const handleAction = (action: ActionType) => {
    onAction(action);
    setActionDropdownOpen(false);
  };

  // Handle overflow action selection
  const handleOverflowAction = (action: OverflowActionType) => {
    onOverflowAction(action);
    setOverflowMenuOpen(false);
  };

  return (
    <header className={`case-header ${isFrozen ? 'frozen' : ''}`}>
      <div className="case-header-container">
        {/* Column 1: Back button */}
        <button
          className="case-header-back"
          onClick={onBack}
          aria-label="Retour à la liste"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>

        {/* Column 2: Main content (identity + actions) */}
        <div className="case-header-main">
          {/* Left side: Identity block */}
          <div className="case-header-identity">
            {/* Row 1: Title + frozen badge */}
            <div className="case-header-row-1">
              <h1 className="case-header-title">Affaire #{affaireId}</h1>

              {/* Frozen badge on mobile */}
              {isFrozen && (
                <span className="case-header-frozen-badge">Gelée</span>
              )}
            </div>
          </div>

          {/* Tab Toggle Switch */}
          {onTabChange && (
            <div className="case-header-tab-toggle" role="tablist" aria-label="Vue">
              <button
                className={`case-header-tab-option ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => onTabChange('activity')}
                role="tab"
                aria-selected={activeTab === 'activity'}
              >
                <ClockIcon className="w-4 h-4" />
                <span>Activité</span>
              </button>
              <button
                className={`case-header-tab-option ${activeTab === 'documents' ? 'active' : ''}`}
                onClick={() => onTabChange('documents')}
                role="tab"
                aria-selected={activeTab === 'documents'}
              >
                <FolderIcon className="w-4 h-4" />
                <span>Documents</span>
              </button>
            </div>
          )}

          {/* Frozen info + Reactivate (when frozen) */}
          {isFrozen && (
            <>
              <div className="case-header-spacer" />
              <div className="case-header-frozen-info">
                {freezeInfo && <span className="case-header-frozen-text">{freezeInfo}</span>}
                {onReactivate && (
                  <button
                    className="case-header-reactivate-btn"
                    onClick={onReactivate}
                  >
                    <PlayIcon className="w-4 h-4" />
                    <span>Réactiver</span>
                  </button>
                )}
              </div>
            </>
          )}

          {/* Right side: Actions */}
          <div className="case-header-actions">
            {/* Assignee dropdown */}
            {assignee && (
              <div className="case-header-assignee">
                <button
                  className="case-header-assignee-btn"
                  onClick={() => onChangeAssignee?.()}
                  disabled={!onChangeAssignee}
                >
                  <div className="case-header-assignee-avatar">
                    {assignee.avatarUrl ? (
                      <img src={assignee.avatarUrl} alt={assignee.name} />
                    ) : (
                      <span>{assignee.initials}</span>
                    )}
                  </div>
                  <span className="case-header-assignee-name">{assignee.name}</span>
                  {onChangeAssignee && (
                    <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </div>
            )}

            {/* Primary action dropdown */}
            <ActionDropdown
              isOpen={actionDropdownOpen}
              onToggle={() => setActionDropdownOpen(!actionDropdownOpen)}
              onSelect={handleAction}
              onClose={() => setActionDropdownOpen(false)}
              isSubscribed={isSubscribed}
            />

            {/* Overflow menu */}
            <OverflowMenu
              isOpen={overflowMenuOpen}
              onToggle={() => setOverflowMenuOpen(!overflowMenuOpen)}
              onSelect={handleOverflowAction}
              onClose={() => setOverflowMenuOpen(false)}
              affaireId={affaireId}
            />

            {/* Mobile sidebar toggle */}
            <button
              className="case-header-sidebar-toggle"
              onClick={onToggleSidebar}
              aria-label="Ouvrir les détails"
            >
              <Bars3Icon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Column 3: Sidebar space for grid alignment */}
        <div className="case-header-sidebar-space" />
      </div>
    </header>
  );
};

export default CaseHeader;
